import React from 'react';
import { getChangeTintClass, renderChangeIndicator } from '../../utils/insightHelpers';
import { calculateChange } from '../../utils/insightHelpers';
import InsightValue from '../InsightValue';
import '../../styles/insightCards.css';

export interface InsightColumnPairProps {
  /** Status quo value for tint/change calculation */
  originalValue?: number;
  /** Current value for tint/change calculation and highlight */
  currentValue?: number;
  /** Status quo column content */
  statusQuoContent: React.ReactNode;
  /** Current column content (can include InsightValue for highlight) */
  currentContent: React.ReactNode;
  /** Show both columns (status quo + adjusted) */
  showStatusQuoColumn?: boolean;
  /** Single column mode (historical) - no tint */
  showSingleColumn?: boolean;
  /** Show percent change indicator */
  showChangeIndicators?: boolean;
  /** Enable highlight when value changes */
  parametersChanged?: boolean;
  /** Click handler for both columns */
  onClick?: () => void;
  /** Columns are clickable */
  clickable?: boolean;
  /** Custom header for status quo column (default: "Status Quo") */
  statusQuoHeader?: React.ReactNode;
  /** Custom header for current column (default: "Adjusted") */
  currentHeader?: React.ReactNode;
  /** Style for the wrapper div (e.g. margin between rows) */
  wrapperStyle?: React.CSSProperties;
  /** Show loading state instead of values */
  isLoading?: boolean;
  /** Optional loading message */
  loadingLabel?: React.ReactNode;
  /** Keep the status quo column visible while the adjusted column is loading */
  keepStatusQuoVisibleWhileLoading?: boolean;
  /** Optional error message shown in place of loading/current values */
  errorMessage?: React.ReactNode;
}

/**
 * Renders status quo and current columns for insight cards.
 * Handles tint class, change indicator, and InsightValue highlight.
 */
export default function InsightColumnPair({
  originalValue,
  currentValue,
  statusQuoContent,
  currentContent,
  showStatusQuoColumn = true,
  showSingleColumn = false,
  showChangeIndicators = false,
  parametersChanged = false,
  onClick,
  clickable = false,
  statusQuoHeader,
  currentHeader,
  wrapperStyle,
  isLoading = false,
  loadingLabel = 'Loading...',
  keepStatusQuoVisibleWhileLoading = true,
  errorMessage,
}: InsightColumnPairProps): React.ReactElement {
  const tintClass = showSingleColumn ? '' : getChangeTintClass(originalValue, currentValue);
  const percentChange = calculateChange(originalValue, currentValue);
  const hasStatusQuoValue = originalValue !== undefined && originalValue !== null;
  const hasError = Boolean(errorMessage);
  const showStatusQuoLoading =
    !hasError &&
    isLoading &&
    (!showStatusQuoColumn ||
      !keepStatusQuoVisibleWhileLoading ||
      !hasStatusQuoValue);
  const loadingContent = (
    <div className="insight-loading-state" aria-live="polite" aria-busy="true">
      <span className="insight-loading-spinner" aria-hidden="true" />
      <span className="insight-loading-label">{loadingLabel}</span>
    </div>
  );
  const errorContent = (
    <div className="insight-error-state" aria-live="polite">
      <span className="insight-error-label">{errorMessage}</span>
    </div>
  );

  return (
    <div className="insight-columns" style={wrapperStyle}>
      {showStatusQuoColumn && (
        <div
          className={`insight-column original ${clickable ? 'clickable-column' : ''}`}
          onClick={onClick}
          style={clickable ? { cursor: 'pointer' } : undefined}
          role={clickable ? 'button' : undefined}
        >
          <div className="column-header">{statusQuoHeader ?? 'Status Quo'}</div>
          <div className="insight-value status-quo-value">
            {showStatusQuoLoading ? loadingContent : hasError && !hasStatusQuoValue ? errorContent : statusQuoContent}
          </div>
        </div>
      )}
      <div
        className={`insight-column current ${tintClass} ${clickable ? 'clickable-column' : ''}`}
        onClick={onClick}
        style={clickable ? { cursor: 'pointer' } : undefined}
        role={clickable ? 'button' : undefined}
      >
        <div className="column-header">{showStatusQuoColumn ? (currentHeader ?? 'Adjusted') : ''}</div>
        {hasError ? (
          <div className="insight-value">{errorContent}</div>
        ) : isLoading ? (
          <div className="insight-value">{loadingContent}</div>
        ) : (
          <InsightValue value={currentValue} parametersChanged={parametersChanged}>
            {currentContent}
            {showChangeIndicators && renderChangeIndicator(percentChange)}
          </InsightValue>
        )}
      </div>
    </div>
  );
}
